import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import { Box, Button, SystemIcon, Typography } from '@wishket/design-system';

import { PATH } from '@/shared/constants';
import { Illust } from '@/shared/Components';

interface CollectionEmptyProps {
  type:
    | 'scrap'
    | 'search'
    | 'empty'
    | 'current'
    | 'private'
    | 'deleted'
    | 'highlights'
    | 'collection';
  search?: string;
}

const EMPTY_MESSAGE_LIST = {
  collection: {
    href: PATH.LIST_NEW,
    title: '컬렉션이 텅 비었어요.',
    buttonName: '새로운 IT 지식 보러가기',
    message: ['비어있는 컬렉션을 IT 지식으로 가득 채워주세요.'],
  },
  current: {
    href: PATH.LIST_NEW,
    title: '방근 본 콘텐츠가 없어요.',
    buttonName: '새로운 IT 지식 보러가기',
    message: ['성장에 도움이 되는 콘텐츠를 읽어보세요.'],
  },
  highlights: {
    href: PATH.LIST_NEW,
    title: '형광펜 한 문장이 없어요.',
    buttonName: '새로운 IT 지식 보러가기',
    message: [
      '원하는 문장을 드래그하고, 떠오르는 아이디어를 메모로 남겨보세요!',
    ],
  },
  scrap: {
    href: PATH.COLLECTION,
    title: '컬렉션이 텅 비었어요.',
    buttonName: '새로운 컬렉션 보러가기',
    message: ['비어있는 컬렉션을 가득 채워주세요.'],
  },
  private: {
    href: PATH.COLLECTION,
    title: '비공개 컬렉션입니다.',
    buttonName: '새로운 컬렉션 보러가기',
    message: ['다른 독자들의 컬렉션을 확인해 보세요.'],
  },
  deleted: {
    href: PATH.COLLECTION,
    title: '삭제된 컬렉션입니다.',
    buttonName: '새로운 컬렉션 보러가기',
    message: ['다른 독자들의 컬렉션을 확인해 보세요.'],
  },
  empty: {
    href: PATH.COLLECTION,
    title: '컬렉션이 없습니다.',
    buttonName: '새로운 컬렉션 보러가기',
    message: ['다른 독자들의 컬렉션을 확인해 보세요.'],
  },
  search: {
    href: null,
    title: '에 대한 검색 결과가 없습니다.',
    buttonName: null,
    message: [
      '단어와 철자가 정확한지 확인해 보세요.',
      '단어 수를 줄인 후 다시 검색해 보세요.',
      '두 단어 이상은 띄어쓰기를 확인해 보세요.',
    ],
  },
};

const CollectionEmpty = ({ type, search }: CollectionEmptyProps) => {
  const { title, message, buttonName, href } = EMPTY_MESSAGE_LIST[type];

  return (
    <Box className="flex w-full flex-col items-center gap-8">
      <Box className="h-[140px] w-[210px] tablet:h-[160px] tablet:w-[240px]">
        <Illust name="hand_pencil" />
      </Box>
      <Box
        className={twMerge(
          'flex flex-col items-center gap-2',
          search && 'gap-8'
        )}
      >
        <Typography variant="subTitle1" className="font-medium text-w-gray-900">
          {search && <span className="text-primary">‘{search}‘</span>}
          {title}
        </Typography>
        {message.length > 1 ? (
          <Box className="flex flex-col gap-1">
            {message.map((item, index) => (
              <Box key={index} className="flex items-center gap-1">
                <SystemIcon
                  name="small_radio_dot"
                  className="size-1 text-w-gray-500"
                />
                <Typography variant="body2" className="text-w-gray-500">
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body1" className=" text-w-gray-700">
            {message}
          </Typography>
        )}
      </Box>
      {buttonName && (
        <Link href={href}>
          <Button variant="solid" className="w-[240px]">
            {buttonName}
          </Button>
        </Link>
      )}
    </Box>
  );
};

export default CollectionEmpty;
